import React, { useState, useEffect, useMemo } from 'react';
import { useStorefrontSettings } from '../../hooks/StorefrontSettingsContext';
import Storefront from '../../services/Storefront';
import Box from '@mui/material/Box';
import Notifications from '../../services/Notifications';
import Loading from '../Loading';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import TextField from '@mui/material/TextField';
import DialogActions from '@mui/material/DialogActions';
import { GridToolbarContainer, GridToolbarFilterButton, GridToolbarExport, GridToolbarDensitySelector } from '@mui/x-data-grid-premium';
import Paper from '@mui/material/Paper';
import { InputLabel, Select, MenuItem } from '@mui/material';
import TranslatableDataGrid from '../shared/TranslatableDataGrid';
import { useStorefrontAdministrationConfig } from '../../hooks/StorefrontAdministrationConfigContext';
import { ThemeProvider } from '@mui/material/styles';
import StorefrontAdminTheme from '../shared/MUIStyles/StorefrontAdminTheme';
import Typography from '@mui/material/Typography';

export const PIMProductAdministration = () => {
    const [translationLocaleData, setTranslationLocaleData] = useState([]);
    const [fetching, setFetching] = useState(false);
    const [selectedLanguage, setSelectedLanguage] = useState('');
    const [openUpdate, setOpenUpdate] = useState(false);
    const [openAdd, setOpenAdd] = React.useState(false);
    const [rows, setRows] = useState([]);
    const [selectedRow, setSelectedRow] = useState(null);
    const storefrontAdministrationConfig = useStorefrontAdministrationConfig();
    const localized = storefrontAdministrationConfig.labels;
    const storeData = useStorefrontSettings();
    const theme = StorefrontAdminTheme;
    const responseMessages = {
        "PartialSuccess": localized.PIMProductPartialSuccess ||'Partial Success',
        "DuplicateKeyViolation": localized.PIMProductDuplicateKeyViolation ||'Duplicate Key Violation',
        "GenericFailure": localized.PIMProductGenericFailure ||'Generic Failure',
        "UpdateSuccess": localized.PIMProductUpdateSuccess ||'Update Success',
        "AddSuccess": localized.PIMProductSuccessfulAdd ||'Add Success'
    };
    const [productGroupData, setProductGroupData] = useState([]);
    const [productGroup1, setProductGroup1] = useState('');
    const [attributeKey, setAttributeKey] = useState('');
    const [labelName, setLabelName] = useState('');
    const [labelValue, setLabelValue] = useState('');

    const [newGroupName, setNewGroupName] = useState('');
    const [isAddingNewGroup, setIsAddingNewGroup] = useState(false);

    useEffect(() => {
        getTranslationLocales();
    }, []);

    const customToolbar = () => {
        return (
            <GridToolbarContainer>
                <InputLabel sx={{ color: "white" }} id="select-language-label">{localized.SelectALanguage}</InputLabel>
                <Select
                    sx={{ background: "#118ABD", borderColor: "white", color: "white", maxHeight: "25px" }}
                    labelId="select-language-label"
                    id="selectedLanguage"
                    value={selectedLanguage}
                    label="Selected Language"
                    onChange={handleLanguageChange}
                >
                    {translationLocaleData && translationLocaleData.languages && translationLocaleData.languages.map((locale) => (
                        <MenuItem key={locale.translationLocaleId} value={locale.translationLocaleId}>{locale.displayName}</MenuItem>
                    ))}
                </Select>
                <Box sx={{ flexGrow: 1 }} />
                <GridToolbarFilterButton
                    slotProps={{
                        tooltip: { title: 'Filter list' }
                    }}
                    variant="outlined"
                    color="update"
                />
                <GridToolbarDensitySelector
                    slotProps={{
                        tooltip: { title: 'Change density' }
                    }}
                    variant="outlined"
                    color="update"
                />

                <GridToolbarExport
                    printOptions={{ disableToolbarButton: true }}
                    slotProps={{
                        tooltip: { title: 'Export data' }
                    }}
                    variant="outlined"
                    color="update"
                />
                <strong>
                    <Button
                        variant="standard"
                        color="success"
                        style={{ marginLeft: 16 }}
                        onClick={(e) => handleClickOpenAdd(e)}
                    >
                        {localized.StorefrontAdminAdd}
                    </Button>
                </strong>
            </GridToolbarContainer>
        );
    }

    const updatePIMAttributeLabel = async (form, type) => {
        const productGroup = isAddingNewGroup ? newGroupName : form.productGroup1;
        var request = {
            attributeKeyID: form.id ?? null,
            attributeKey: form.attributeKey ?? attributeKey,
            groupName: productGroup,
            labelName: form.labelName ?? labelName,
            labelValue: form.labelValue,
            translationlocaleId: selectedLanguage,
            operationType: type
        };
        return await Storefront.updatePIMAttributeLabel(request).then((res) => {
            if (res) {
                const notificationType = res.response.endsWith("Success") ? "message" : "error";
                const message = responseMessages[res.response];

                if (message) {
                    Notifications[notificationType](message);
                }
                else {
                    Notifications.error(localized.LabelGenericFailure);
                }
                getPIMAttributeLabels(selectedLanguage);
            }
        });
    }

    const handleLanguageChange = (event) => {
        setSelectedLanguage(event.target.value);
        getPIMAttributeLabels(event.target.value);
    }

    const getTranslationLocales = () => {
        setFetching(true);
        Storefront.getStorefrontTranslationLocales(storeData.storefrontNumber).then(loadTranslations);
    }

    const getPIMAttributeLabels = (translationLocaleId) => {
        Storefront.getPIMAttributeLabels(storeData.storefrontNumber, translationLocaleId).then(loaded);
    }

    const handleClickOpenUpdate = (e, row) => {
        e.stopPropagation();       
        setProductGroup1(row.groupName || '');

        setAttributeKey(row.attributeKey || '');
        setLabelName(row.labelName || '');
        setLabelValue(row.labelValue || '');
        setIsAddingNewGroup(false);
        setNewGroupName('');
        setSelectedRow(row);
        setOpenUpdate(true);
    };
    const handleProductGroupChange = (event) => {
        const value = event.target.value;
        if (value === 'new') {
            setIsAddingNewGroup(true);
        } else {
            setIsAddingNewGroup(false);
        }
        setProductGroup1(value);
    }
    const handleCloseUpdate = () => {
        setOpenUpdate(false);
        setSelectedRow(null);
        clearElements();
    };   
    const loadTranslations = (data) => {
        if (data) {
            setTranslationLocaleData(data)
            setSelectedLanguage(data.languages[0].translationLocaleId);
            getPIMAttributeLabels(data.languages[0].translationLocaleId);
        }
        else {
            setTranslationLocaleData([])
        }
        setFetching(false);
    }

    const loaded = (data) => {
        if (data) {
            mapRows(data);
        }
        setProductGroupData(data.productGroups);
    }

    const mapRows = (data) => {
        var rows = [];
        data.pimAttributeLabels.forEach((pimAttributeLabels) => {
            rows.push({
                id: pimAttributeLabels.attributeKeyID,
                groupName: pimAttributeLabels.groupName, attributeKey: pimAttributeLabels.attributeKey,
                labelName: pimAttributeLabels.labelName, labelValue: pimAttributeLabels.labelValue
            });
        });
        setRows(rows);
    }

    const handleClickOpenAdd = (e) => {
        e.stopPropagation();
        setOpenAdd(true);
    };
    const handleCloseAdd = () => {
        setOpenAdd(false);
        clearElements();
    };
    const clearElements = () => {
        setProductGroup1("");
        setNewGroupName("");
        setAttributeKey("");
        setLabelName("");
        setLabelValue("");
        setIsAddingNewGroup(false);
        setNewGroupName('');
    };
    const renderUpdateButton = (row) => {
        return (    
            <>
                <Dialog
                    open={openUpdate}
                    onClose={handleCloseUpdate}
                    PaperProps={{
                        component: 'form',
                        onSubmit: (event) => {
                            event.preventDefault();
                            const formData = new FormData(event.currentTarget);
                            formData.append("id", row.id);
                            const formJson = Object.fromEntries(formData.entries());
                            updatePIMAttributeLabel(formJson, "update");
                            handleCloseUpdate();
                        },
                    }}
                >
                    <DialogTitle><h1>{localized.UpdatePIMProduct}</h1></DialogTitle>
                    <DialogContent>
                        <DialogContentText>
                            <h3>{localized.ValuesEligibleForUpdateBelow}</h3>
                        </DialogContentText>
                        <InputLabel required id="productGroup1-label">{"Product Group"}</InputLabel>
                        <Select
                            fullWidth
                            labelId="productGroup1-label"
                            id="productGroup1"
                            name="productGroup1"
                            value={productGroup1}
                            label={localized.PIMProductGroup || "Product Group"}
                            onChange={handleProductGroupChange}                            
                        >
                            <MenuItem value=""><em>-No Group-</em></MenuItem>
                            {productGroupData && productGroupData.map((productGroups) => (
                                <MenuItem key={productGroups.name} value={productGroups.name}>{productGroups.name}</MenuItem>
                            ))}
                        </Select>

                        <TextField
                            margin="dense"
                            id="attributeKey"
                            name="attributeKey"
                            label={localized.PIMAttributeKey || "Attribute Key"}
                            type="text"
                            fullWidth
                            variant="standard"
                            defaultValue={row.attributeKey}
                            disabled={true}
                            required
                        />
                        <TextField
                            margin="dense"
                            id="labelName"
                            name="labelName"
                            label={localized.PIMLabelName || "Label Key"}
                            type="text"
                            fullWidth
                            variant="standard"
                            defaultValue={row.labelName}
                            disabled={!!labelName}
                        />
                        <TextField                           
                            margin="dense"
                            id="labelValue"
                            name="labelValue"
                            label={localized.LabelValue}
                            type="text"
                            fullWidth
                            variant="standard"
                            defaultValue={row.labelValue}
                            inputProps={{ maxLength: 250 }}
                        />
                    </DialogContent>
                    <DialogActions>
                        <Button variant="standard" color="error" onClick={handleCloseUpdate}>{localized.StorefrontAdminCancel}</Button>
                        <Button variant="standard" color="update" type="submit">{localized.StorefrontAdminUpdate}</Button>
                    </DialogActions>
                </Dialog>
            </>
        )
    }

    const renderAddButton = () => {
        return (
            <>
                <Dialog
                    open={openAdd}
                    onClose={handleCloseAdd}
                    PaperProps={{
                        component: 'form',
                        style: {
                            width: "400px"
                        },
                        onSubmit: (event) => {
                            event.preventDefault();
                            const formData = new FormData(event.currentTarget);
                            const formJson = Object.fromEntries(formData.entries());
                            updatePIMAttributeLabel(formJson, "add");
                            handleCloseAdd();
                        },
                    }}
                >
                    <DialogTitle><h1>{localized.AddNewPIMProduct}</h1></DialogTitle>
                    <DialogContent>
                        <DialogContentText>
                            <h3>{localized.EnterDetailsBelow}</h3>
                        </DialogContentText>
                        <InputLabel required id="productGroup1-label">{"Product Group"}</InputLabel>
                        <Select
                            fullWidth
                            labelId="productGroup1-label"
                            id="productGroup1"
                            name="productGroup1"
                            value={productGroup1}
                            onChange={handleProductGroupChange}
                        >
                            <MenuItem value=""><em>-No Group-</em></MenuItem>
                            {productGroupData && productGroupData.map((group) => (
                                <MenuItem key={group.name} value={group.name}>{group.name}</MenuItem>
                            ))}
                            <MenuItem value="new"><em>Add New Group</em></MenuItem>
                        </Select>

                        {isAddingNewGroup && (
                            <TextField
                                margin="dense"
                                id="newGroupName"
                                name="newGroupName"
                                label={localized.PIMProductGroup || "New Product Group"} 
                                type="text"
                                fullWidth
                                variant="standard"
                                value={newGroupName}
                                onChange={(e) => setNewGroupName(e.target.value)}
                                inputProps={{ maxLength: 50 }}
                                required
                            />
                        )}

                        <TextField
                            margin="dense"
                            id="attributeKey"
                            name="attributeKey"
                            label={localized.PIMAttributeKey || "Attribute Key"}
                            type="text"
                            fullWidth
                            variant="standard"
                            value={attributeKey}
                            onChange={(e) => setAttributeKey(e.target.value)}
                            inputProps={{ maxLength: 50 }}
                            required
                        />
                        <TextField
                            margin="dense"
                            id="labelName"
                            name="labelName"
                            label={localized.PIMLabelName || "Label Key"}
                            type="text"
                            fullWidth
                            variant="standard"
                            value={labelName}
                            onChange={(e) => setLabelName(e.target.value)}
                            inputProps={{ maxLength: 50 }}
                        />
                        <TextField
                            margin="dense"
                            id="labelValue"
                            name="labelValue"
                            label={localized.LabelValue}
                            type="text"
                            fullWidth
                            variant="standard"
                            value={labelValue}
                            onChange={(e) => setLabelValue(e.target.value)}
                            inputProps={{ maxLength: 250 }}                            
                        />
                    </DialogContent>
                    <DialogActions>
                        <Button variant="standard" color="error" onClick={handleCloseAdd}>
                            {localized.StorefrontAdminCancel}
                        </Button>
                        <Button variant="standard" color="primary" type="submit">
                            {localized.StorefrontAdminAdd}
                        </Button>
                    </DialogActions>
                </Dialog>
            </>
        );
    };

    const columns = [
        {
            field: 'groupName',
            headerName: localized.PIMProductGroup || "Product Group",
            headerAlign: 'left',
            flex: 1
        },
        {
            field: 'attributeKey',
            headerName: localized.PIMAttributeKey || "Attribute Key",
            headerAlign: 'left',
            flex: 1
        },
        {
            field: 'labelName',
            headerName: localized.PIMLabelName || 'Label Key',
            headerAlign: 'left',
            flex: 1
        },
        {
            field: 'labelValue',
            headerName: localized.LabelValue,
            headerAlign: 'left',
            flex: 1
        },
        {
            field: "updateButton",
            headerName: localized.StorefrontAdminUpdate,
            headerAlign: 'center',
            align: "center",
            renderCell: (params) => {
                return (
                    <strong>
                        <Button
                            variant="standard"
                            color="update"
                            style={{ marginLeft: 16 }}
                            onClick={(e) => handleClickOpenUpdate(e, params.row)}
                        >
                            {localized.StorefrontAdminUpdate}
                        </Button>
                    </strong>
                )
            },
            disableClickEventBubbling: true
        }
    ];
    const PIMProductItems = useMemo(() => {
        return (
            <Paper sx={{ height: 700, width: '100%' }}>
                <TranslatableDataGrid rows={rows} columns={columns} insetToolbar={customToolbar} labels={localized} />
            </Paper>
        );
    }, [rows, localized]);

    return (
        <>
            {
                fetching && <Loading type="brand" />
            }
            {
                !fetching && (localized && Object.keys(localized).length > 0) &&
                <>
                    <ThemeProvider theme={theme}>
                        <Typography variant="h1">{localized.PIMProductAdministrationHeader}</Typography>                    
                        {PIMProductItems}
                        {openUpdate && renderUpdateButton(selectedRow)}
                        {openAdd && renderAddButton()}
                    </ThemeProvider>
                </>
            }
        </>


    );
}

export default PIMProductAdministration;

